/**
 * Endpoints de **Logística → Ingresos Extras** (`/api/logistica/ingresos`).
 *
 * El listado va paginado en el servidor con los filtros del panel anterior
 * (fechas y monto mínimo) más el buscador libre, y la edición y la inhabilitación
 * se piden por ingreso.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type { IngresoEditable, IngresosFiltros, IngresosRespuesta } from './ingresos.types';

export const ingresosKeys = {
  lista: (filtros: IngresosFiltros) => ['logistica', 'ingresos', filtros] as const,
};

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: IngresosFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.montoMin) parametros.set('monto_min', filtros.montoMin);
  if (filtros.search) parametros.set('search', filtros.search);
  if (filtros.orden) parametros.set('orden', filtros.orden);
  if (filtros.direccion) parametros.set('direccion', filtros.direccion);
  if (filtros.page) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const ingresosApi = {
  listar: (filtros: IngresosFiltros = {}, signal?: AbortSignal): Promise<IngresosRespuesta> =>
    apiClient.get<IngresosRespuesta>(`/api/logistica/ingresos${cadenaDe(filtros)}`, { signal }),

  editar: (id: number, ingreso: IngresoEditable): Promise<{ message: string }> =>
    apiClient.put<{ message: string }>(`/api/logistica/ingresos/${id}`, {
      body: {
        descripcion: ingreso.descripcion,
        cantidad: Number(ingreso.cantidad),
        fecha: ingreso.fecha,
      },
    }),

  eliminar: (id: number): Promise<{ message: string; id: number }> =>
    apiClient.delete<{ message: string; id: number }>(`/api/logistica/ingresos/${id}`),
};

/** Ingresos adicionales del filtro pedido. */
export function useIngresos(filtros: IngresosFiltros = {}) {
  return useQuery({
    queryKey: ingresosKeys.lista(filtros),
    queryFn: ({ signal }) => ingresosApi.listar(filtros, signal),
  });
}

/** Guarda los cambios de un ingreso y refresca el listado. */
export function useEditarIngreso() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ingreso }: { id: number; ingreso: IngresoEditable }) =>
      ingresosApi.editar(id, ingreso),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['logistica', 'ingresos'] });
    },
  });
}

/** Inhabilita un ingreso y refresca el listado. */
export function useEliminarIngreso() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => ingresosApi.eliminar(id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['logistica', 'ingresos'] });
    },
  });
}
